import { parseBoilerGraphFromRecognizedText } from './boiler-graph-text-parse.util';
import {
  BoilerGraphExtractionConfig,
  BoilerGraphJsonMetadata,
  DEFAULT_BOILER_GRAPH_PROFILES,
  deriveMetricsFromProfile,
} from './boiler-graph-extract.util';

const OCR_MIN_EDGE_PX = 900;

async function upscaleImageForOcr(file: File, minEdge: number): Promise<File> {
  const isImage =
    file.type.startsWith('image/') ||
    /\.(png|jpe?g|webp|bmp|tif?f)$/i.test(file.name);
  if (!isImage || !file.size) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest < minEdge ? minEdge / Math.max(longest, 1) : 1;
    if (scale <= 1) {
      bitmap.close();
      return file;
    }

    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return file;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((value) => resolve(value), 'image/png');
    });
    if (!blob?.size) {
      return file;
    }

    const base = file.name.replace(/\.[^.]+$/i, '') || 'table';
    return new File([blob], `${base}-ocr.png`, { type: 'image/png' });
  } catch {
    return file;
  }
}

declare global {
  interface Window {
    Tesseract?: {
      recognize: (
        image: File | string,
        lang?: string,
        options?: { logger?: (m: unknown) => void },
      ) => Promise<{ data: { text: string } }>;
    };
  }
}

let tesseractLoadPromise: Promise<void> | null = null;

function loadTesseractScript(): Promise<void> {
  if (window.Tesseract) {
    return Promise.resolve();
  }
  if (tesseractLoadPromise) {
    return tesseractLoadPromise;
  }
  tesseractLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load OCR engine'));
    document.head.appendChild(script);
  });
  return tesseractLoadPromise;
}

export async function extractTextFromBoilerGraphImage(
  file: File,
): Promise<string> {
  await loadTesseractScript();
  if (!window.Tesseract) {
    return '';
  }

  const recognize = async (source: File): Promise<string> => {
    try {
      const result = await window.Tesseract!.recognize(source, 'eng', {
        logger: () => undefined,
      });
      return result?.data?.text ?? '';
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (/too small/i.test(message)) {
        return '';
      }
      throw error;
    }
  };

  const prepared = await upscaleImageForOcr(file, OCR_MIN_EDGE_PX);
  const text = await recognize(prepared);
  if (text.trim()) {
    return text;
  }

  if (prepared !== file) {
    return recognize(file);
  }

  return '';
}

/** Map OCR text to known profile when manufacturer is detected. */
export function findBoilerProfileFromOcrText(
  text: string,
  profiles: BoilerGraphExtractionConfig[] = DEFAULT_BOILER_GRAPH_PROFILES,
): BoilerGraphExtractionConfig | null {
  const upper = text.toUpperCase();
  if (/KANGRIM|KAN[\s-]*GRIM/.test(upper)) {
    return (
      profiles.find((p) => p.manufacturer === 'KANGRIM') ??
      profiles.find((p) => p.label.toUpperCase().includes('KANGRIM')) ??
      null
    );
  }
  if (/AALBORG|MISSION/.test(upper)) {
    const capMatch = text.match(/(\d{4,6})\s*KG|(\d{1,2})\s*TON/i);
    let capacity = 20000;
    if (capMatch) {
      capacity = capMatch[1]
        ? Number(capMatch[1])
        : Number(capMatch[2]) * 1000;
    }
    return (
      profiles.find(
        (p) =>
          p.manufacturer === 'AALBORG INDUSTRIES' &&
          p.capacityKgPerHour === capacity,
      ) ??
      profiles.find((p) => p.manufacturer === 'AALBORG INDUSTRIES') ??
      null
    );
  }
  if (/LOAD\s*%|LOAD\s*12|STEAM\s*CAPA/i.test(upper)) {
    return profiles.find((p) => p.graphAxisType === 'loadPercent') ?? null;
  }
  return null;
}

/** Build metadata from a matched built-in profile (curve data), not noisy OCR numbers. */
export function metadataFromMatchedProfile(
  profile: BoilerGraphExtractionConfig,
): BoilerGraphJsonMetadata {
  const derived = deriveMetricsFromProfile(profile);
  return {
    label: profile.label,
    manufacturer: profile.manufacturer,
    model: profile.model,
    capacityKgPerHour: profile.capacityKgPerHour,
    graphAxisType: profile.graphAxisType,
    steamProductionRangeMin: profile.steamProductionRangeMin,
    steamProductionRangeMax: profile.steamProductionRangeMax,
    loadCurve: profile.loadCurve,
    efficiencyCurve: profile.efficiencyCurve,
    maxEfficiencyPercent: derived.maxEfficiencyPercent,
    steamProductionKgH: derived.steamProductionKgH,
  };
}

export async function extractBoilerMetadataFromImageOcr(
  file: File,
  profiles: BoilerGraphExtractionConfig[] = DEFAULT_BOILER_GRAPH_PROFILES,
): Promise<{
  meta: BoilerGraphJsonMetadata | null;
  ocrText: string;
  matchedProfile: BoilerGraphExtractionConfig | null;
}> {
  try {
    const ocrText = await extractTextFromBoilerGraphImage(file);
    if (!ocrText.trim()) {
      return { meta: null, ocrText: '', matchedProfile: null };
    }

    const matchedProfile = findBoilerProfileFromOcrText(ocrText, profiles);

    if (matchedProfile?.loadCurve?.length || matchedProfile?.efficiencyCurve?.length) {
      return {
        meta: metadataFromMatchedProfile(matchedProfile),
        ocrText,
        matchedProfile,
      };
    }

    const meta = parseBoilerGraphFromRecognizedText(ocrText);
    if (!meta?.capacityKgPerHour) {
      return { meta: null, ocrText, matchedProfile };
    }

    return { meta, ocrText, matchedProfile };
  } catch {
    return { meta: null, ocrText: '', matchedProfile: null };
  }
}
